'use client';

import { Button } from '@curology/ui-components-web/react/Button';
import { CardFooter } from '@curology/ui-components-web/react/Card/CardFooter';
import { CardPricing } from '@curology/ui-components-web/react/Card/CardPricing';
import { CardRating } from '@curology/ui-components-web/react/Card/CardRating';
import { ProductCard } from '@curology/ui-components-web/react/Card/ProductCard';
import { ProductCardContent } from '@curology/ui-components-web/react/Card/ProductCard/ProductCardContent';
import { AgencyMinusIcon } from '@curology/ui-components-web/react/Icon/icons/AgencyMinusIcon';
import { AgencyPlusIcon } from '@curology/ui-components-web/react/Icon/icons/AgencyPlusIcon';
import { MinusIcon } from '@curology/ui-components-web/react/Icon/icons/MinusIcon';
import { PlusIcon } from '@curology/ui-components-web/react/Icon/icons/PlusIcon';
import { Label } from '@curology/ui-components-web/react/Label';
import { Typography } from '@curology/ui-components-web/react/Typography';
import clsx from 'clsx';
import React, { useMemo } from 'react';

import { useDataProvider } from '@/components/flow/components/DataProvider/useDataProvider';
import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { useCardTrialSizeItemPrices } from '@/components/product-selection/hooks/useCardTrialSizeItemPrices';
import { useProductIngredientsModalState } from '@/components/product-selection/hooks/useProductIngredientsModalState';
import { useSelectedItemIds } from '@/components/product-selection/hooks/useSelectedItemIds';
import { useSubmittingForm } from '@/components/product-selection/hooks/useSubmittingForm';
import { DATA_KEYS } from '@/consts/dataKeys';
import {
  getCardFullSizeItemPrices,
  getDisplayPrices,
  getSalePriceSubscription,
} from '@/helpers/productSelectionAndCheckout/helpers';
import { type Card } from '@/types/productSelectionAndCheckout/types';

import ProductCardImageCarousel from '../../productCardImageCarousel';
import ViewOnlyProductOptions from '../../viewOnlyProductOptions';

const brandMinusIcons = {
  curology: MinusIcon,
  agency: AgencyMinusIcon,
} as const;

const brandPlusIcons = {
  curology: PlusIcon,
  agency: AgencyPlusIcon,
} as const;

const getAspectRatio = (fullWidth?: boolean, isHorizontal?: boolean) => {
  if (fullWidth) {
    return '3:2';
  } else if (isHorizontal) {
    return '4:5';
  }

  return '3:4';
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export function OtcCard({
  card,
  fullWidth,
  isHorizontal,
}: {
  card: Card;
  checkboxSelector?: boolean;
  fullWidth?: boolean;
  isHorizontal?: boolean;
}) {
  const { brand } = useFlowContext();
  const cardTrialSizeItemPrices = useCardTrialSizeItemPrices(card);
  const { isCardSelected, toggleCard } = useSelectedItemIds();
  const { flags, patient, fullSizeItemPrices } = useDataProvider(
    DATA_KEYS.productSelection,
  );
  const { setModalCard } = useProductIngredientsModalState();
  const { submittingForm } = useSubmittingForm();
  const cardFullSizeItemPrices = getCardFullSizeItemPrices(
    card,
    fullSizeItemPrices,
  );

  const cardSelected = useMemo(
    () => isCardSelected(card),
    [card, isCardSelected],
  );

  const BrandMinusIcon = brandMinusIcons[brand];
  const BrandPlusIcon = brandPlusIcons[brand];

  const { price, salePrice } = getDisplayPrices(
    cardTrialSizeItemPrices,
    cardFullSizeItemPrices,
  );

  return (
    <li
      className={clsx(
        'cur-pb-4 cur-pr-4',
        !fullWidth && !isHorizontal && 'cur-basis-1/2',
      )}
      key={card.id}
    >
      <ProductCard className="cur-flex-nowrap cur-h-full">
        <ProductCardContent
          className="cur-cursor-pointer"
          data-sentry-id="Show product(s) modal"
          horizontal={isHorizontal}
          image={
            <ProductCardImageCarousel
              aspectRatio={getAspectRatio(fullWidth, isHorizontal)}
              card={card}
              patient={patient}
              showRecommendationTag={!isHorizontal}
            />
          }
          meta={
            <>
              {isHorizontal && card.recommendationText ? (
                <div>
                  <Label>{card.recommendationText.label}</Label>
                </div>
              ) : null}
              <div className="cur-flex cur-flex-wrap cur-items-center">
                <CardPricing
                  className={
                    flags.isTwoMonthTrialWith50PctOff ? 'cur-mr-1' : ''
                  }
                  price={price}
                  salePrice={salePrice}
                  salePriceSubscription={
                    salePrice
                      ? getSalePriceSubscription(card, flags)
                      : undefined
                  }
                />
                {flags.isTwoMonthTrialWith50PctOff && (
                  <Typography className="cur-text-brand" size="meta">
                    (50% off)
                  </Typography>
                )}
              </div>
              {card.metaSubtext ? (
                <Typography size="meta">{card.metaSubtext}</Typography>
              ) : null}
            </>
          }
          onClick={() => setModalCard(card)}
          onKeyPress={(event) => {
            if (event.key === 'Enter') {
              setModalCard(card);
            }
          }}
          rating={
            card.numReviews && card.rating ? (
              <CardRating
                count={card.numReviews}
                showCount={false}
                size="sm"
                value={card.rating}
              />
            ) : undefined
          }
          tabIndex={0}
          title={card.name}
        >
          {card.bodyDescription && (
            <Typography>{card.bodyDescription}</Typography>
          )}
          {card.selectableOptions ? (
            <ViewOnlyProductOptions
              selectableOptions={card.selectableOptions}
            />
          ) : null}
        </ProductCardContent>
        <CardFooter className="-cur-mt-2 cur-pb-1 cur-pl-1 cur-pr-1">
          <Button
            buttonType={cardSelected ? 'secondary' : 'primary'}
            data-sentry-id="Toggle product(s) selection"
            disabled={submittingForm}
            iconStart={cardSelected ? BrandMinusIcon : BrandPlusIcon}
            onClick={() => {
              toggleCard({
                card,
                patient,
              });
            }}
          >
            {cardSelected ? 'Remove' : 'Add'}
          </Button>
        </CardFooter>
      </ProductCard>
    </li>
  );
}
